// banking/src/components/dashboard/swap/CryptoToFiatModal.tsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { X } from 'lucide-react';
import { swapService } from '../../../services/swap.service';
import type { RootState } from '../../../store';

interface CryptoToFiatModalProps {
  isOpen: boolean;
  onClose: () => void;
  zenusAccountId?: string;
  onSuccess: () => void;
}

interface WalletInfo {
  address: string;
  balance: string;
  currency: string;
  displayName: string;
  network: string;
}

interface FiatWallet {
  id: string;
  name: string;
  balance: number;
  currency: string;
}

const CryptoToFiatModal: React.FC<CryptoToFiatModalProps> = ({
  isOpen,
  onClose,
  zenusAccountId,
  onSuccess
}) => {
  const { t } = useTranslation();
  const { data: { cryptoWallets, zenusAccount } } = useSelector((state: RootState) => state.assets);

  const [selectedCryptoWallet, setSelectedCryptoWallet] = useState<string>('');
  const [selectedFiatWallet, setSelectedFiatWallet] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [cryptoWalletsList, setCryptoWalletsList] = useState<WalletInfo[]>([]);
  const [fiatWallets, setFiatWallets] = useState<FiatWallet[]>([]);

  // Cargar wallets cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      loadWallets();
    } else {
      // Reset form
      setSelectedCryptoWallet('');
      setSelectedFiatWallet('');
      setAmount('');
    }
  }, [isOpen, zenusAccount, cryptoWallets]);

  const loadWallets = () => {
    // Cargar Crypto Wallets
    if (cryptoWallets?.data) {
      const cryptoList: WalletInfo[] = [];

      // Bitcoin - Show all wallets, even with 0 balance
      (cryptoWallets.data.bitcoin || []).forEach((wallet: any) => {
        if (wallet.balances?.btc !== undefined) {
          cryptoList.push({
            address: wallet.address,
            balance: wallet.balances.btc,
            currency: 'BTC',
            displayName: 'Bitcoin (BTC)',
            network: 'BITCOIN'
          });
        }
      });

      // Ethereum + tokens - Show all wallets, even with 0 balance
      (cryptoWallets.data.ethereum || []).forEach((wallet: any) => {
        if (wallet.balances) {
          if (wallet.balances.eth !== undefined) {
            cryptoList.push({
              address: wallet.address,
              balance: wallet.balances.eth,
              currency: 'ETH',
              displayName: 'Ethereum (ETH)',
              network: 'ETHEREUM'
            });
          }
          if (wallet.balances.usdt_erc20 !== undefined) {
            cryptoList.push({
              address: wallet.address,
              balance: wallet.balances.usdt_erc20,
              currency: 'USDT_ERC20',
              displayName: 'USDT (ERC20)',
              network: 'ETHEREUM'
            });
          }
          if (wallet.balances.usdc_erc20 !== undefined) {
            cryptoList.push({
              address: wallet.address,
              balance: wallet.balances.usdc_erc20,
              currency: 'USDC_ERC20',
              displayName: 'USDC (ERC20)',
              network: 'ETHEREUM'
            });
          }
        }
      });

      // BNB + tokens - Show all wallets, even with 0 balance
      (cryptoWallets.data.bnb || []).forEach((wallet: any) => {
        if (wallet.balances) {
          if (wallet.balances.bnb !== undefined) {
            cryptoList.push({
              address: wallet.address,
              balance: wallet.balances.bnb,
              currency: 'BNB',
              displayName: 'BNB Chain (BNB)',
              network: 'BNB'
            });
          }
          if (wallet.balances.usdt_bep20 !== undefined) {
            cryptoList.push({
              address: wallet.address,
              balance: wallet.balances.usdt_bep20,
              currency: 'USDT_BEP20',
              displayName: 'USDT (BEP20)',
              network: 'BNB'
            });
          }
        }
      });

      // Tron + tokens - Show all wallets, even with 0 balance
      (cryptoWallets.data.tron || []).forEach((wallet: any) => {
        if (wallet.balances) {
          if (wallet.balances.trx !== undefined) {
            cryptoList.push({
              address: wallet.address,
              balance: wallet.balances.trx,
              currency: 'TRX',
              displayName: 'Tron (TRX)',
              network: 'TRON'
            });
          }
          if (wallet.balances.usdt_trc20 !== undefined) {
            cryptoList.push({
              address: wallet.address,
              balance: wallet.balances.usdt_trc20,
              currency: 'USDT_TRC20',
              displayName: 'USDT (TRC20)',
              network: 'TRON'
            });
          }
        }
      });

      // Solana + tokens - Show all wallets, even with 0 balance
      (cryptoWallets.data.solana || []).forEach((wallet: any) => {
        if (wallet.balances) {
          if (wallet.balances.sol !== undefined) {
            cryptoList.push({
              address: wallet.address,
              balance: wallet.balances.sol,
              currency: 'SOL',
              displayName: 'Solana (SOL)',
              network: 'SOLANA'
            });
          }
          if (wallet.balances.usdt_spl !== undefined) {
            cryptoList.push({
              address: wallet.address,
              balance: wallet.balances.usdt_spl,
              currency: 'USDT_SPL',
              displayName: 'USDT (Solana)',
              network: 'SOLANA'
            });
          }
        }
      });

      setCryptoWalletsList(cryptoList);
    }

    // Cargar Fiat Wallets (actualmente solo Zenus USD)
    const fiatList: FiatWallet[] = [];

    if (zenusAccount && zenusAccount.account && zenusAccount.balance !== undefined) {
      fiatList.push({
        id: zenusAccount.account.id || 'zenus-usd',
        name: 'USA Account',
        balance: zenusAccount.balance,
        currency: 'USD'
      });
    }

    setFiatWallets(fiatList);

    // Auto-seleccionar la primera (y única por ahora) wallet fiat
    if (fiatList.length > 0) {
      setSelectedFiatWallet(fiatList[0].id);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validaciones
    if (!selectedCryptoWallet) {
      toast.error(t('swap.cryptoToFiat.errors.selectCryptoWallet') || 'Please select a crypto wallet');
      return;
    }

    if (!selectedFiatWallet) {
      toast.error(t('swap.cryptoToFiat.errors.selectFiatWallet') || 'Please select a USD account');
      return;
    }

    const amountNum = parseFloat(amount);
    if (!amount || isNaN(amountNum)) {
      toast.error(t('swap.cryptoToFiat.errors.invalidAmount') || 'Invalid amount');
      return;
    }

    if (amountNum <= 0) {
      toast.error(t('swap.cryptoToFiat.errors.positiveAmount') || 'Amount must be greater than 0');
      return;
    }

    const selectedCrypto = cryptoWalletsList.find(w => `${w.currency}-${w.address}` === selectedCryptoWallet);
    if (!selectedCrypto) {
      toast.error(t('swap.cryptoToFiat.errors.walletNotFound') || 'Wallet not found');
      return;
    }

    // Validar balance suficiente
    if (amountNum > parseFloat(selectedCrypto.balance)) {
      toast.error(t('swap.cryptoToFiat.errors.insufficientBalance') || 'Insufficient balance');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await swapService.createCryptoToFiat({
        toAccountId: selectedFiatWallet,
        fromAmount: amountNum,
        fromCurrency: selectedCrypto.currency,
        fromWalletAddress: selectedCrypto.address
      });

      if (response.success) {
        toast.success(t('swap.cryptoToFiat.success') || 'SWAP request created successfully');
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      console.error('Error creating SWAP:', error);
      toast.error(error.message || t('swap.cryptoToFiat.errors.generic') || 'Error creating SWAP request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMaxAmount = () => {
    const selectedCrypto = cryptoWalletsList.find(w => `${w.currency}-${w.address}` === selectedCryptoWallet);
    if (selectedCrypto) {
      setAmount(selectedCrypto.balance);
    }
  };

  if (!isOpen) return null;

  const selectedCrypto = cryptoWalletsList.find(w => `${w.currency}-${w.address}` === selectedCryptoWallet);
  const selectedFiat = fiatWallets.find(w => w.id === selectedFiatWallet);

  // Calcular equivalencia USD aproximada (solo para stablecoins)
  const getUsdEquivalence = () => {
    if (!selectedCrypto || !amount) return null;

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum)) return null;

    // Para stablecoins, 1:1 con USD
    if (selectedCrypto.currency.includes('USDT') || selectedCrypto.currency.includes('USDC')) {
      return amountNum;
    }

    return null; // Para otras cryptos, no mostramos equivalencia (necesitaría precio en tiempo real)
  };

  const usdEquiv = getUsdEquivalence();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">
            {t('swap.cryptoToFiat.title') || 'SWAP: CRYPTO → FIAT'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* From: Crypto Wallet */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('swap.cryptoToFiat.fromWallet') || 'From (Crypto Wallet)'} <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedCryptoWallet}
              onChange={(e) => setSelectedCryptoWallet(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              required
            >
              <option value="">{t('swap.cryptoToFiat.selectWallet') || 'Select crypto wallet'}</option>
              {cryptoWalletsList.map((wallet) => {
                const walletBalance = parseFloat(wallet.balance);
                const isDisabled = walletBalance <= 0;
                return (
                  <option
                    key={`${wallet.currency}-${wallet.address}`}
                    value={`${wallet.currency}-${wallet.address}`}
                    disabled={isDisabled}
                    style={{ color: isDisabled ? '#999' : 'inherit' }}
                  >
                    {wallet.displayName} - {wallet.address.slice(0, 6)}...{wallet.address.slice(-4)} - {walletBalance.toFixed(8)} {isDisabled ? '(Insufficient balance)' : ''}
                  </option>
                );
              })}
            </select>
            {cryptoWalletsList.length === 0 && (
              <p className="text-xs text-red-500 mt-1">
                {t('swap.cryptoToFiat.errors.noCryptoWallets') || 'You don\'t have any crypto wallets. Please create one first.'}
              </p>
            )}
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('swap.cryptoToFiat.amountLabel') || 'Amount'} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="0"
                step="0.00000001"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder={t('swap.cryptoToFiat.enterAmount') || 'Enter amount'}
                required
              />
              <button
                type="button"
                onClick={handleMaxAmount}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600 hover:text-green-700 font-semibold text-sm"
              >
                MAX
              </button>
            </div>
            {usdEquiv && (
              <p className="text-xs text-gray-600 mt-1">
                {t('swap.cryptoToFiat.equivalence') || 'Approx. USD equivalent'}: ${usdEquiv.toFixed(2)} USD
              </p>
            )}
          </div>

          {/* To: USD Account */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('swap.cryptoToFiat.toAccount') || 'To (USD Account)'} <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedFiatWallet}
              onChange={(e) => setSelectedFiatWallet(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              required
            >
              <option value="">{t('swap.cryptoToFiat.selectAccount') || 'Select account'}</option>
              {fiatWallets.map((wallet) => (
                <option key={wallet.id} value={wallet.id}>
                  {wallet.name} - ${wallet.balance.toFixed(2)} {wallet.currency}
                </option>
              ))}
            </select>
          </div>

          {/* Summary */}
          {selectedCrypto && selectedFiat && amount && parseFloat(amount) > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-900 mb-2">
                {t('swap.summary.title') || 'Summary'}
              </h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-700">{t('swap.summary.from') || 'From'}:</span>
                  <span className="font-medium">{selectedCrypto.displayName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">{t('swap.summary.amount') || 'Amount'}:</span>
                  <span className="font-medium">{parseFloat(amount).toFixed(8)} {selectedCrypto.currency.split('_')[0]}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">{t('swap.summary.address') || 'Address'}:</span>
                  <span className="font-mono text-xs">{selectedCrypto.address.slice(0, 10)}...{selectedCrypto.address.slice(-8)}</span>
                </div>
                {usdEquiv && (
                  <div className="flex justify-between">
                    <span className="text-gray-700">{t('swap.summary.usdValue') || 'USD Value (approx.)'}:</span>
                    <span className="font-medium">${usdEquiv.toFixed(2)} USD</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-700">{t('swap.summary.to') || 'To'}:</span>
                  <span className="font-medium">{selectedFiat.name}</span>
                </div>
                <div className="border-t border-green-300 pt-2 mt-2">
                  <p className="text-xs text-green-700">
                    {t('swap.cryptoToFiat.info.commission') || 'Commission: ~2% (adjustable by staff)'}
                  </p>
                  <p className="text-xs text-green-700">
                    {t('swap.cryptoToFiat.info.processing') || 'Processing time: 1-24 hours after confirmation'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm font-semibold text-yellow-800 mb-2">
              {t('swap.cryptoToFiat.warning.title') || 'Next Steps:'}
            </p>
            <ol className="list-decimal ml-4 mt-1 text-yellow-700 space-y-1 text-sm">
              <li>{t('swap.cryptoToFiat.warning.step1') || 'Submit this request'}</li>
              <li>{t('swap.cryptoToFiat.warning.step2') || 'Our team will provide a destination wallet address via ticket'}</li>
              <li>{t('swap.cryptoToFiat.warning.step3') || 'Send the crypto to the provided address'}</li>
              <li>{t('swap.cryptoToFiat.warning.step4') || 'We will credit USD to your USA Account after confirmation'}</li>
            </ol>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              {t('common.cancel') || 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || cryptoWalletsList.length === 0}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting
                ? (t('common.loading') || 'Loading...')
                : (t('swap.cryptoToFiat.submit') || 'Create SWAP Request')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CryptoToFiatModal;
